import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Text,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetChatChannels } from "@src/appV2/Chat/ChatList/hooks/useGetChatChannels";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useCallback } from "react";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";

import {
  type CreateShiftChatChannelRequest,
  useCreateChatChannel,
} from "./api/useCreateChatChannel";
import { type ShiftBlockDetail } from "./api/useGetShiftBlock";
import { CancelShiftBlockIconButton } from "./CancelShiftBlockIconButton";
import { ShiftBlockItem } from "./ShiftBlockItem";
import { ShiftBlockShiftDetails } from "./ShiftBlockShiftDetails";
import { SHIFT_BLOCK_ACTIONS, transformIntoShiftBlockWithDetails } from "./utils";

interface FacilityChatListItemProps {
  shiftBlockId: string;
  facilityId: string;
  agentId: string;
  isShiftBlockAssigned: boolean;
}

function FacilityChatListItem(props: FacilityChatListItemProps) {
  const { shiftBlockId, agentId, facilityId, isShiftBlockAssigned } = props;
  const history = useHistory();
  const { showErrorToast } = useToast();
  const { isLoading: isLoadingChatChannels, data: chatChannelsData } = useGetChatChannels();
  const { path: currentRouterUrl } = useRouteMatch();
  const { mutateAsync: createChatChannel } = useCreateChatChannel<CreateShiftChatChannelRequest>();

  const currentChannel = chatChannelsData?.channels?.find(
    (channel) => channel.url === `${facilityId}_${agentId}`
  );
  const hasExistingChannel = isDefined(currentChannel?.url);

  const handleClick = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.FACILITY_CHAT_TAPPED,
      shiftBlockId,
      facilityId,
      isShiftBlockAssigned,
      chatChannelPresent: hasExistingChannel,
      location: currentRouterUrl,
    });

    if (hasExistingChannel) {
      history.push(generatePath(CHAT_PAGE_FULL_PATH, { facilityId }));
      return;
    }

    try {
      await createChatChannel({ facilityId, agentId });
      history.push(generatePath(CHAT_PAGE_FULL_PATH, { facilityId }));
    } catch {
      showErrorToast("Failed to create chat channel. Please try again.");
    }
  }, [
    facilityId,
    agentId,
    hasExistingChannel,
    shiftBlockId,
    isShiftBlockAssigned,
    currentRouterUrl,
    history,
    createChatChannel,
    showErrorToast,
  ]);

  if (isLoadingChatChannels) {
    return null;
  }

  return (
    <ListItemButton divider onClick={handleClick}>
      <ListItemIcon iconType="chat" />
      <ListItemText primary="Chat with facility" secondary="Have any questions about this block?" />
    </ListItemButton>
  );
}

interface ShiftBlockDetailProps {
  worker: Worker;
  shiftBlock: ShiftBlockDetail;
}

export function ShiftBlockDetails(props: ShiftBlockDetailProps) {
  const { worker, shiftBlock } = props;
  const { path: currentRouterUrl } = useRouteMatch();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shiftBlock.data.attributes.facilityId,
  });

  const shiftBlockWithMappedDetails = transformIntoShiftBlockWithDetails(
    shiftBlock.data,
    shiftBlock.included
  );
  const {
    id: shiftBlockId,
    facility,
    isShiftBlockAssigned,
    priorityAccess,
  } = shiftBlockWithMappedDetails;

  useLogEffect(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
    action: SHIFT_BLOCK_ACTIONS.BLOCK_DETAILS_VIEWED,
    shiftBlockId,
    facilityId: facility.id,
    isShiftBlockAssigned,
    location: currentRouterUrl,
    priorityAccess,
  });

  return (
    <Stack spacing={4}>
      <ShiftBlockItem
        isDetailView
        shouldShowFacilityDetails
        shiftBlock={shiftBlockWithMappedDetails}
        workerTimezone={worker.tmz}
      />

      <List
        sx={{
          "& .MuiListItem-divider": {
            borderColor: (theme) => theme.palette.border?.primary,
          },
          "& .MuiListItemButton-root > .MuiBox-root:last-child": {
            backgroundColor: (theme) => theme.palette.border?.primary,
          },
        }}
      >
        {isDefined(shiftBlock.data.attributes.notesForProfessionals) && (
          <ListItem divider>
            <ListItemIcon iconType="info" />
            <ListItemText
              disableTypography
              primary={
                <Text semibold variant="h6">
                  Block Information
                </Text>
              }
              secondary={
                <Text
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                  }}
                >
                  {shiftBlock.data.attributes.notesForProfessionals}
                </Text>
              }
            />
          </ListItem>
        )}

        <FacilityChatListItem
          shiftBlockId={shiftBlockId}
          agentId={worker.userId}
          facilityId={facility.id}
          isShiftBlockAssigned={isShiftBlockAssigned}
        />
        <ListItem divider>
          <ListItemIcon iconType="calendar-clock" />
          <ListItemText
            disableTypography
            primary={
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Text semibold variant="h6">
                  Block Shifts
                </Text>
                <CancelShiftBlockIconButton shiftBlockWithDetails={shiftBlockWithMappedDetails} />
              </Stack>
            }
            secondary={
              <Stack sx={{ marginTop: 4 }}>
                <ShiftBlockShiftDetails
                  shiftBlockWithDetails={shiftBlockWithMappedDetails}
                  agentTimezone={worker.tmz}
                  mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
                />
              </Stack>
            }
          />
        </ListItem>
      </List>
    </Stack>
  );
}
